import { Button } from "components/Button/Button";
import { DataTable } from "components/DataTable/DataTable";
import { useLangContext } from "context/Lang";
import { UserCheck, Users, XIcon } from "lucide-react";
import { Dropdown } from "primereact/dropdown";
import { useEffect, useState } from "react";
import { routes } from "routes";
import { AsyncProcess } from "services/AsyncProcess";


type AddEditorDialogContentProps={
    onChangeEditorsIds:(e:number[])=>void;
    initEditorIds?:number[]
}


export const AddEditorDialogContent = ({
    onChangeEditorsIds,
    initEditorIds
}:AddEditorDialogContentProps) => {
    const [loading, setLoading] = useState<boolean>(false);
    const [loadingSemesters, setLoadingSemesters] = useState<boolean>(false);
    const [loadingCourses, setLoadingCourses] = useState<boolean>(false);

    const [academicYears, setAcademicYears] = useState<AcademicYearType[]>([]);
    const [semesters, setSemesters] = useState<SemesterType[]>([]);
    const [courses, setCourses] = useState<CourseType[]>([]);

    const [selectedYear, setSelectedYear] = useState<AcademicYearType | null>(null);
    const [selectedSemester, setSelectedSemester] = useState<SemesterType | null>(null);
    const [selectedCourse, setSelectedCourse] = useState<CourseType | null>(null);

    const [branches, setBranches] = useState<BranchType[]>([]);
    const [selectedBranch, setSelectedBranch] = useState<BranchType | null>(null);

    const [students, setStudents] = useState<UserType[]>([]);

    const [responsiblesIds, setResponsiblesIds] = useState<number[]>([]);

    const { gW } = useLangContext();


    const getStudents = () => {
        if (!selectedBranch?.id || !selectedSemester?.id) {
            setStudents([]);
            return;
        }
        setLoading(true);
        AsyncProcess({
            type: "get",
            url: `${routes.api.students}/branch`,
            data:{
                params:{
                    branchId:selectedBranch?.id,
                    semesterId:selectedSemester?.id
                }
            },
            setError: () => setLoading(false),
            effectProcess: [(res: any) => {
                if (Array.isArray(res)) {
                    setStudents(res);
                } else if (res?.data) {
                    setStudents(res.data);
                }
                setLoading(false);
            }]
        });
    };
    // 1. جلب الأعوام الدراسية
    const getAcademicYear = () => {
        setLoading(true);
        AsyncProcess({
            type: "get",
            url: routes.api.academicYear,
            setError: () => setLoading(false),
            effectProcess: [(res: any) => {
                if (res?.success !== false) {
                    setAcademicYears(res?.data || res);
                }
                setLoading(false);
            }]
        });
    };

    // 2. جلب الفصول الدراسية بناءً على السنة
    const getSemesters = () => {
        if (!selectedYear?.id) {
            setSemesters([]);
            setSelectedSemester(null);
            return;
        }
        setLoadingSemesters(true);
        AsyncProcess({
            type: "get",
            url: `${routes.api.semesters}?academicyearid=${selectedYear.id}`,
            setError: () => setLoadingSemesters(false),
            effectProcess: [(res: any) => {
                if (res?.success !== false) {
                    setSemesters(res?.data || res);
                }
                setLoadingSemesters(false);
            }]
        });
    };

    // 3. جلب المواد بناءً على الفصل الدراسي
    const getCourses = () => {
        if (!selectedSemester?.id) {
            setCourses([]);
            setSelectedCourse(null);
            return;
        }
        setLoadingCourses(true);
        AsyncProcess({
            type: "get",
            url: `${routes.api.courses}/my?semesterid=${selectedSemester.id}`,
            setError: () => setLoadingCourses(false),
            effectProcess: [(res: any) => {
                if (res?.success !== false) {
                    setCourses(res?.data || res);
                }
                setLoadingCourses(false);
            }]
        });
    };

    useEffect(() => {
        getAcademicYear();
    }, []);

    useEffect(() => {
        getSemesters();
    }, [selectedYear]);

    useEffect(() => {
        getCourses();
    }, [selectedSemester]);

    const getBranches = () => {
        if (!selectedCourse?.id || !selectedSemester?.id) {
            setBranches([]);
            setSelectedBranch(null);
            return;
        }

        setLoading(true);
        AsyncProcess({
            type: "get",
            url: `${routes.api.branches}`,
            data:{
                params:{
                    courseid:selectedCourse.id,
                    semesterid:selectedSemester.id
                }
            },
            setError: () => setLoading(false),
            effectProcess: [
                (res: any) => {
                    if(res?.success===false){

                    }else {
                        const data = res?.data;
                        if (Array.isArray(data)) {
                            setBranches(data);
                        }
                    }
                    setLoading(false);
                },
            ],
        });
    };

        // تفعيل الجلب عند تغيير المادة المختارة
    useEffect(() => {
        getBranches();
    }, [selectedCourse]);



    useEffect(()=>{
        onChangeEditorsIds(responsiblesIds)
    },[responsiblesIds])

    useEffect(() => { getStudents(); }, [selectedBranch]);

    const handleSelectResponsible = (user: UserType) => {
        if (user.id && !responsiblesIds.includes(user.id)) {
            setResponsiblesIds([...responsiblesIds, user.id]);
        }
    };

    const handleRemoveResponsible = (id: number) => {
        setResponsiblesIds(responsiblesIds.filter(rid => rid !== id));
    };

    const columns: any[] = [
        {
            label: gW("name"),
            name: "name",
            cell: (user: UserType) => (
                <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-bold text-xs">
                        {user.name?.[0]}{user.surname?.[0]}
                    </div>
                    <div className="flex flex-col">
                        <span className="text-sm font-medium">{user.name} {user.surname}</span>
                        <span className="text-xs text-primary-400">{user.userName}</span>
                    </div>
                </div>
            )
        },
        {
            label: gW("username"),
            name: "username"
        },
        {
            label: gW("actions"),
            cell: (user: UserType) => (
                <Button
                    type="button"
                    onClick={() => handleSelectResponsible(user)}
                    className="p-button-outlined p-button-sm flex gap-2 items-center"
                >
                    <UserCheck size={14} />
                    {gW("select")}
                </Button>
            )
        }
    ];

    const selectedResponsibles = students.filter(u => u.id && responsiblesIds.includes(u.id));


    console.log(academicYears);


    return (
        <div className="min-h-80">
            <div className="p-fluid grid">
                <div className="h-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 w-full">

                   
                   
                   
                    
                    
                    <Dropdown
                        panelClassName=" border  rounded-lg shadow-xl mt-1 "
                        id="academicYear"
                        filter={true}
                        className="w-full rounded-lg  shadow-sm hover:border-primary/50 focus:border-primary  "
                        loading={loading}
                        value={selectedYear}
                        onChange={(e) => {
                            setSelectedYear(e.value);
                            setSelectedSemester(null);
                            setSelectedCourse(null);
                        }}
                        options={academicYears}
                        optionLabel="name"
                        placeholder={gW("select-year")}
                        style={{ width: "100%" }}
                        emptyMessage={gW("no-years-found")}
                        showClear={!!selectedYear}
                    />

                    <Dropdown
                        id="semester"
                        filter
                        loading={loadingSemesters}
                        disabled={!selectedYear}
                        value={selectedSemester}
                        onChange={(e) => {
                            setSelectedSemester(e.value);
                            setSelectedCourse(null);
                        }}
                        options={semesters}
                        optionLabel="name"
                        placeholder={selectedYear ? gW("select-semester") : gW("select-year-first")}
                        style={{ width: "100%" }}
                        emptyMessage={gW("no-semesters-found")}
                        showClear={!!selectedSemester}
                    />

                    <Dropdown
                        id="course"
                        filter
                        loading={loadingCourses}
                        disabled={!selectedSemester}
                        value={selectedCourse}
                        onChange={(e) => setSelectedCourse(e.value)}
                        options={courses}
                        optionLabel="name" 
                        placeholder={selectedSemester ? gW("select-course") : gW("select-semester-first")}
                        style={{ width: "100%" }}
                        emptyMessage={gW("no-courses-found")}
                        showClear={!!selectedCourse}
                        itemTemplate={(option) => (
                            <div className="flex justify-between w-full">
                                <span>{option.name}</span>
                                <span className="opacity-50 text-xs ml-2">[{option.code}]</span>
                            </div>
                        )}
                    />
                    <Dropdown
                        id="branch"
                        filter
                        loading={loading}
                        disabled={!selectedCourse}
                        value={selectedBranch}
                        onChange={(e) => setSelectedBranch(e.value)}
                        options={branches}
                        optionLabel="title" 
                        placeholder={selectedCourse ? gW("select-branch") : gW("select-course-first")}
                        style={{ width: "100%" }}
                        emptyMessage={gW("no-branches-found")}
                        showClear={!!selectedBranch}
                        itemTemplate={(option: BranchType) => (
                            <div>{gW("branch-short")} {option.title}</div>
                        )}
                    />
                </div>
            </div>

            <div className="flex flex-col gap-2 my-2 ">
                <h3 className="text-sm font-semibold flex items-center gap-2 text-primary-700">
                    <Users size={16} />
                    {gW("selected-responsibles")} ({selectedResponsibles.length})
                </h3>
                <div className="flex flex-wrap gap-2 min-h-[50px] p-3 bg-primary-50 rounded-xl border border-dashed border-primary-300">
                    {selectedResponsibles.length === 0 ? (
                        <span className="text-xs text-primary-400 italic self-center">{gW("no-selection-yet")}</span>
                    ) : (
                        selectedResponsibles.map(r => (
                            <div key={r.id} className="flex items-center gap-2 bg-white border border-blue-200 px-3 py-1.5 rounded-full shadow-sm">
                                <span className="text-sm font-medium">{r.name} {r.surname}</span>
                                <button
                                    onClick={() => r.id && handleRemoveResponsible(r.id)}
                                    className="text-primary-400 hover:text-red-500 transition-colors"
                                >
                                    <XIcon size={14} />
                                </button>
                            </div>
                        ))
                    )}
                </div>
            </div>

            {/* جدول الطلاب المتاحين للاختيار */}
            <div className="border rounded-xl overflow-hidden bg-white shadow-sm">
                <DataTable
                    data={students.filter(u => u.id && ![...responsiblesIds,...initEditorIds||[]].includes(u.id))}
                    columns={columns}
                    loading={loading}
                    rowKey={(user: UserType) => user.id?.toString() || ""}
                    rows={5}
                />
            </div>
        </div>
    );
}